/* eslint-disable max-lines */

import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, effect, inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { FormArray, FormsModule, NonNullableFormBuilder, ReactiveFormsModule } from '@angular/forms';
import { BsModalService } from 'ngx-bootstrap/modal';
import { TooltipDirective } from 'ngx-bootstrap/tooltip';
import { filter } from 'rxjs';
import {
  AppShieldSensitivityPickerComponent,
  PullZoneWafConfigVariableModel,
  WAFExecutionMode,
  WAFExecutionModeEnum,
  WAFPayloadLimitActionEnum,
  WafMappedEnumList,
} from '@bn/app-shield';
import { PlanType } from '@bn/app-shield/enums';
import { AppRoutes } from '@bn/dashboard-core/enums';
import { AbstractLazyDirective } from '@bn/shared/entities/abstracts/abstract-lazy.directive';
import { StoreRequest } from '@bn/shared/state-store';
import { MarkAllTouchedDirective } from '@bn/web-ui';
import { CardComponent } from '@bn/web-ui/card/card.component';
import { ConfirmationDialogComponent } from '@bn/web-ui/dialog';
import { FilterItem } from '@bn/web-ui/filter/models';
import { LevelComponent } from '@bn/web-ui/level/level.component';
import { Level } from '@bn/web-ui/level/models';
import { LoadingComponent } from '@bn/web-ui/loading/loading.component';
import { FormListItem } from '@bn/web-ui/models';
import { SelectOptions } from '@bn/web-ui/select/models';
import { SelectComponent } from '@bn/web-ui/select/select.component';
import { RadioTabsComponent } from '@bn/web-ui/tabs/radio-tabs.component';
import { WAFEngineConfig } from '../../../../models';
import { MapWafEnumToFormListItems, MapArrayOfStringsToSelectItemsPipe } from '../../../../pipes';
import { ShieldApiStore } from '../../../../stores';

@Component({
  templateUrl: './app-shield-waf-general.page.html',
  styleUrls: ['./app-shield-waf-general.page.scss'],
  imports: [
    MapWafEnumToFormListItems,
    MapArrayOfStringsToSelectItemsPipe,
    AppShieldSensitivityPickerComponent,
    CardComponent,
    LevelComponent,
    LoadingComponent,
    RadioTabsComponent,
    SelectComponent,
    FormsModule,
    ReactiveFormsModule,
    TooltipDirective,
    MarkAllTouchedDirective,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppShieldWafGeneralPage extends AbstractLazyDirective implements OnDestroy {
  private readonly dialog = inject(BsModalService);

  readonly #shieldStore = inject(ShieldApiStore);
  readonly #changeDetectorRef = inject(ChangeDetectorRef);

  public shield = this.#shieldStore.selectedEntity();
  public isEngineModeUpdating = this.#shieldStore.isLoading(StoreRequest.Update, ['EngineMode']);
  public isResponseBodyLimitActionUpdating = this.#shieldStore.isLoading(StoreRequest.Update, ['responseBodyLimitAction']);
  public isRequestBodyLimitActionUpdating = this.#shieldStore.isLoading(StoreRequest.Update, ['requestBodyLimitAction']);
  public isLoadingToggleWaf = this.#shieldStore.isLoading(StoreRequest.Update, ['wafEnabled']);
  public isLoadingToggleRealtimeThreatIntelligence = this.#shieldStore.isLoading(StoreRequest.Update, ['wafRealtimeThreatIntelligenceEnabled']);
  public isLoadingRuleSensitivity = this.#shieldStore.isLoading(StoreRequest.Update, ['ruleSensitivity']);

  public isLoading = {
    allowed_http_versions: this.#shieldStore.isLoading(StoreRequest.Update, ['allowed_http_versions']),
    allowed_methods: this.#shieldStore.isLoading(StoreRequest.Update, ['allowed_methods']),
    allowed_request_content_type: this.#shieldStore.isLoading(StoreRequest.Update, ['allowed_request_content_type']),
    detection_paranoia_level: this.#shieldStore.isLoading(StoreRequest.Update, ['detection_paranoia_level']),
    executing_paranoia_level: this.#shieldStore.isLoading(StoreRequest.Update, ['executing_paranoia_level']),
    blocking_paranoia_level: this.#shieldStore.isLoading(StoreRequest.Update, ['blocking_paranoia_level']),
  };

  public activeTemplate = -1;

  private formInitialPatchComplete = {
    allowed_http_versions: false,
    allowed_methods: false,
    allowed_request_content_type: false,
  };

  public isFirstTime = true;
  public engineModes?: WafMappedEnumList;
  public allowed_http_versions: string[] = [];
  public allowed_methods: string[] = [];
  public allowed_request_content_type: string[] = [];

  public filter: FilterItem[] = [];
  public options: Partial<SelectOptions> = {
    clearable: false,
    closeOnSelect: false,
    isSearchable: false,
    multiple: true,
    hideName: false, // only badge is shown
    searchPlaceholder: 'HTTP Methods',
    maxSelectedItems: 20,
    showEndSelectedIcon: true,
    dropdownWidth: '100',
    minWidth: '100px',
    showOnlyCount: false,
    hideClear: true,
  };

  public levelIndexes: { [key: string]: number } = {
    detection_paranoia_level: 0,
    executing_paranoia_level: 0,
    blocking_paranoia_level: 0,
  };

  public levelTemplates = [
    {
      title: 'Low',
      description: 'Recommended for most websites. Catches critical threats with minimal false positives.',
      illustration: '/assets/images/bunny-shield-protection-level-asleep.svg',
      levelIndexes: {
        detection_paranoia_level: 0,
        executing_paranoia_level: 0,
        blocking_paranoia_level: 0,
      },
    },
    {
      title: 'Medium',
      description: 'Adds extra protection against common attacks. May cause occasional false positives.',
      illustration: '/assets/images/bunny-shield-protection-level-low.svg',
      levelIndexes: {
        detection_paranoia_level: 1,
        executing_paranoia_level: 1,
        blocking_paranoia_level: 1,
      },
    },
    {
      title: 'High',
      description: 'Aggressively blocks more advanced threats. Increased chance of false positives.',
      illustration: '/assets/images/bunny-shield-protection-level-medium.svg',
      levelIndexes: {
        detection_paranoia_level: 2,
        executing_paranoia_level: 2,
        blocking_paranoia_level: 2,
      },
    },
    {
      title: 'Extreme',
      description: 'Maximum security mode. Best for high-risk apps, but expect frequent false positives.',
      illustration: '/assets/images/bunny-shield-protection-level-high.svg',
      levelIndexes: {
        detection_paranoia_level: 3,
        executing_paranoia_level: 3,
        blocking_paranoia_level: 3,
      },
    },
    {
      title: 'Custom',
      description: 'Tailor rules to your exact needs. Ideal for advanced users with specific requirements.',
      illustration: '/assets/images/bunny-shield-protection-level-extreme.svg',
      levelIndexes: {
        detection_paranoia_level: 3,
        executing_paranoia_level: 3,
        blocking_paranoia_level: 3,
      },
    },
  ];

  public levelLabels: Level[] = [{ label: 'Level 1' }, { label: 'Level 2' }, { label: 'Level 3' }, { label: 'Level 4' }];

  public fb = inject(NonNullableFormBuilder);

  public requestBodyLimitActionItems: FormListItem[] = [];
  public responseBodyLimitActionItems: FormListItem[] = [];

  public form = this.fb.group({
    wafExecutionMode: this.fb.control(WAFExecutionModeEnum.Logging),
    requestBodyLimitAction: this.fb.control(WAFPayloadLimitActionEnum.Log),
    responseBodyLimitAction: this.fb.control(WAFPayloadLimitActionEnum.Ignore),
    allowed_http_versions: this.fb.control([]),
    allowed_methods: this.fb.control([]),
    allowed_request_content_type: this.fb.control([]),
  });

  constructor() {
    super();

    const pullZoneId = this.#shieldStore.pullZoneId();

    this.contentService.setTitle('WAF');
    this.contentService.setBackButton({
      routerLink: `/${AppRoutes.CDN}/${pullZoneId}/${AppRoutes.Shield}`,
    });

    this.setEngineModes();
    this.setEngineConfig();
    this.setSensitivityTemplate();
    this.subscribeToStoreSignals();
    this.subscribeToFormChangeEvents();

    this.form.get('wafExecutionMode')?.valueChanges.subscribe((value) => {
      this.onEngineTabChange(value);
    });

    this.form.get('requestBodyLimitAction')?.valueChanges.subscribe((value) => {
      this.onRequestBodyLimitActionChange(value);
    });

    this.form.get('responseBodyLimitAction')?.valueChanges.subscribe((value) => {
      this.onResponseBodyLimitActionChange(value);
    });

    this.requestBodyLimitActionItems = this.getEnumItems();
    this.responseBodyLimitActionItems = this.getEnumItems();
  }

  public ngOnDestroy(): void {
    this.contentService.setBackButton(null);
  }

  public getEnumItems() {
    return Object.keys(WAFPayloadLimitActionEnum)
      .filter((key) => isNaN(Number(key)))
      .map((key) => ({
        label: key,
        name: key,
        value: WAFPayloadLimitActionEnum[key as keyof typeof WAFPayloadLimitActionEnum],
      }));
  }

  public onTemplatePickerClick({ index }: { index: number }): void {
    this.onTemplatePickerChange(index);
  }

  public onTemplatePickerChange(templateIndex: number, updateLevelIndexes = true): void {
    // Note: in case user decrease sensitivity or they select custom we dont need to show confirmation dialog
    if (templateIndex === 4 || templateIndex < this.activeTemplate) {
      this.onTemplatePickerConfirm(templateIndex, updateLevelIndexes);
    } else {
      const confirmDialogRef = this.dialog.show(ConfirmationDialogComponent, {
        class: 'modal-dialog-centered modal-sm',
        initialState: {
          data: {
            contextType: 'danger',
            icon: 'file-shield',
            confirmButtonName: 'Yes, Continue',
            description: `Increasing sensitivity may cause false positives. To avoid blocking legitimate traffic, we recommend enabling <strong>logging mode</strong> first to monitor results, then re-enable <strong>blocking mode</strong> once you're confident in the rule behavior.`,
            event: {
              action: 'confirm',
            },
            title: 'Increasing rule sensitivity?',
            textCenter: true,
          },
        },
      });

      this.subscriptions.add(
        confirmDialogRef.content?.getOnAction$().subscribe((event) => {
          if (event.action === 'cancel') {
            confirmDialogRef.hide();

            return;
          }

          if (event.action === 'confirm') {
            this.onTemplatePickerConfirm(templateIndex, updateLevelIndexes);
          }
        }),
      );
    }
  }

  // NOTE: this is confusing af. Try to agree to change API so we will not have so much complexity on FE
  public onTemplatePickerConfirm(templateIndex: number, updateLevelIndexes = true): void {
    if (templateIndex === this.activeTemplate) return;
    this.activeTemplate = templateIndex;

    if (!updateLevelIndexes || templateIndex === this.levelTemplates.length - 1) return;

    const activeTemplate = this.levelTemplates[templateIndex];

    this.levelIndexes.detection_paranoia_level = activeTemplate.levelIndexes.detection_paranoia_level;
    this.levelIndexes.executing_paranoia_level = activeTemplate.levelIndexes.executing_paranoia_level;
    this.levelIndexes.blocking_paranoia_level = activeTemplate.levelIndexes.blocking_paranoia_level;

    let wafEngineConfig = this.onSensibilityChange('detection_paranoia_level', this.levelIndexes.detection_paranoia_level, false, false);

    wafEngineConfig = this.onSensibilityChange('executing_paranoia_level', this.levelIndexes.executing_paranoia_level, false, false, wafEngineConfig);

    wafEngineConfig = this.onSensibilityChange('blocking_paranoia_level', this.levelIndexes.blocking_paranoia_level, false, false, wafEngineConfig);

    const id = this.shield()?.shieldZoneId;

    if (!id) return;

    // Note: We save it separately so we can update all loaders
    this.#shieldStore.updateShield({
      id,
      data: { wafEngineConfig },
      customFields: ['ruleSensitivity'],
    });
  }

  // NOTE: this is confusing af. Try to agree to change API so we will not have so much complexity on FE
  public onSensibilityChange(which: string, level: number, doSave = true, changeTemplate = true, existingWafEngineConfig?: PullZoneWafConfigVariableModel[]) {
    if (changeTemplate) this.onTemplatePickerChange(this.levelTemplates.length - 1, false); // Select last template = custom

    const id = this.shield()?.shieldZoneId;
    if (!id) return;

    const wafEngineConfig = this.setEngineConfigItem(
      { name: which, valueEncoded: String(level + 1) },
      existingWafEngineConfig || this.shield()?.wafEngineConfig || [],
    );

    this.levelIndexes[which] = +level;

    if (!doSave) return wafEngineConfig;

    this.#shieldStore.updateShield({
      id,
      data: { wafEngineConfig },
      customFields: [which],
    });

    return wafEngineConfig;
  }

  // NOTE: this is confusing af. Try to agree to change API so we will not have so much complexity on FE
  private setEngineConfigItem(newData: WAFEngineConfig, existingConfig: PullZoneWafConfigVariableModel[]) {
    if (existingConfig.filter(({ name }) => name === newData.name).length) {
      // item already exists - replace it
      const index = existingConfig.findIndex(({ name }) => name === newData.name);
      if (index === -1) return;

      existingConfig[index] = newData;
    } else {
      // doesn't exist yet
      existingConfig.push(newData);
    }

    return existingConfig;
  }

  public onCardInputChange(value: boolean, key: string): void {
    const shieldId = this.#shieldStore.idSelected();

    if (!shieldId) return;

    this.#shieldStore.updateShield({
      id: +shieldId,
      data: {
        [key]: value,
      },
      customFields: [key],
    });
  }

  public onRequestBodyLimitActionChange(wafRequestBodyLimitAction: WAFPayloadLimitActionEnum): void {
    const shieldId = this.#shieldStore.idSelected();

    if (!shieldId) return;

    this.#shieldStore.updateShield({
      id: +shieldId,
      data: {
        wafRequestBodyLimitAction,
      },
      customFields: ['requestBodyLimitAction'],
      toastr: {
        message: `Request body limit action changed to <strong>${WAFPayloadLimitActionEnum[wafRequestBodyLimitAction]}</strong>.`,
      },
    });
  }

  public onResponseBodyLimitActionChange(wafResponseBodyLimitAction: WAFPayloadLimitActionEnum): void {
    const shieldId = this.#shieldStore.idSelected();

    if (!shieldId) return;

    this.#shieldStore.updateShield({
      id: +shieldId,
      data: {
        wafResponseBodyLimitAction,
      },
      customFields: ['responseBodyLimitAction'],
      toastr: {
        message: `Response body limit action changed to <strong>${WAFPayloadLimitActionEnum[wafResponseBodyLimitAction]}</strong>.`,
      },
    });
  }

  public onEngineTabChange(value: WAFExecutionMode): void {
    const shieldId = this.#shieldStore.idSelected();

    if (!shieldId) return;

    if (this.shield()?.learningMode && value === WAFExecutionModeEnum.Blocking) {
      this.confirmEngineModeChangeInLearningMode();

      return;
    }

    if (this.shield()?.wafExecutionMode === value) return;

    this.#shieldStore.updateShield({
      id: +shieldId,
      data: {
        wafExecutionMode: value,
      },
      customFields: ['EngineMode'],
      toastr: {
        message: `Rule Execution Mode changed to <strong>${
          new MapWafEnumToFormListItems().transform(this.engineModes).find((formListItem) => formListItem.value === value)?.name
        }</strong>.`,
      },
    });
  }

  private confirmEngineModeChangeInLearningMode(): void {
    const confirmDialogRef = this.dialog.show(ConfirmationDialogComponent, {
      class: 'modal-dialog-centered modal-sm',
      initialState: {
        data: {
          contextType: 'danger',
          confirmButtonName: 'Yes, Continue',
          description:
            'You are currently in <strong>learning mode</strong>, please ensure you have minimal false positives before enabling blocking mode for requests that trigger a rule.',
          event: {
            action: 'confirm',
          },
          title: 'Turn on blocking mode?',
          textCenter: true,
        },
      },
    });

    this.subscriptions.add(
      confirmDialogRef.content?.getOnAction$().subscribe((event) => {
        if (event.action === 'cancel') {
          this.form.get('wafExecutionMode')?.setValue(WAFExecutionModeEnum.Logging, { emitEvent: false });

          this.#changeDetectorRef.markForCheck();

          this.dialog.hide();
        }

        if (event.action === 'confirm') {
          const id = this.shield()?.shieldZoneId;

          if (!id) return;

          this.#shieldStore.updateShield({
            id,
            data: { learningMode: false, wafExecutionMode: WAFExecutionModeEnum.Blocking },
            toastr: {
              message: `WAF Execution Mode changed to <strong>Blocking</strong>.`,
            },
          });
        }
      }),
    );
  }

  private setEngineModes() {
    toObservable(this.#shieldStore.wafEnums).subscribe((wafEnums) => {
      this.engineModes = wafEnums?.find((apiEnum) => apiEnum.enumName === 'WAFExecutionMode') ?? <WafMappedEnumList>[];
    });
  }

  private subscribeToFormChangeEvents() {
    this.form
      .get('allowed_http_versions')
      ?.valueChanges.pipe(filter(() => this.formInitialPatchComplete.allowed_http_versions))
      .subscribe((value) => {
        this.handleAllowedProperties(value, 'allowed_http_versions');
      });
    this.form
      .get('allowed_methods')
      ?.valueChanges.pipe(filter(() => this.formInitialPatchComplete.allowed_methods))
      .subscribe((value) => {
        this.handleAllowedProperties(value, 'allowed_methods');
      });
    this.form
      .get('allowed_request_content_type')
      ?.valueChanges.pipe(filter(() => this.formInitialPatchComplete.allowed_request_content_type))
      .subscribe((value) => {
        this.handleAllowedProperties(value, 'allowed_request_content_type');
      });
  }

  private handleAllowedProperties(value: string[], property: 'allowed_http_versions' | 'allowed_methods' | 'allowed_request_content_type'): void {
    const id = this.#shieldStore.selectedEntityId()();
    if (!id) return;

    const originalWafEngineConfig = this.shield()?.wafEngineConfig ?? [];
    const originalAllowedHttpVersionsString = originalWafEngineConfig.find(({ name }) => name === property);

    const newAllowedHttpVersionsString = {
      name: property,
      valueEncoded: property !== 'allowed_request_content_type' || !value.length ? value.join(' ') : `|${value.join('| |')}|`,
    };

    if (!originalAllowedHttpVersionsString) {
      originalWafEngineConfig.push(newAllowedHttpVersionsString);
    } else {
      // get index of existing
      const index = originalWafEngineConfig.findIndex(({ name }) => name === property);

      // change existing
      originalWafEngineConfig[index] = newAllowedHttpVersionsString;

      // if (value.length) {
      //   // change existing
      //   originalWafEngineConfig[index] = newAllowedHttpVersionsString;
      // } else {
      //   // NOTE: this doesn't clear on API
      //   originalWafEngineConfig.splice(index, 1);
      // }
    }

    this.#shieldStore.updateShield({
      id: Number(id),
      data: { wafEngineConfig: originalWafEngineConfig },
      customFields: [property],
    });
  }

  private setEngineConfig(): void {
    effect(() => {
      this.allowed_request_content_type =
        this.#shieldStore
          .engineConfig()
          .find(({ name }) => name === 'allowed_request_content_type')
          ?.valueEncoded.split(' ')
          .map((item: string) => item.replace(/\|/g, '')) ?? [];

      this.allowed_http_versions =
        this.#shieldStore
          .engineConfig()
          .find(({ name }) => name === 'allowed_http_versions')
          ?.valueEncoded.split(' ') ?? [];

      this.allowed_methods =
        this.#shieldStore
          .engineConfig()
          .find(({ name }) => name === 'allowed_methods')
          ?.valueEncoded.split(' ') ?? [];
    });
  }

  private setSensitivityTemplate(): void {
    effect(() => {
      const wafEngineConfig = this.#shieldStore.entity()?.wafEngineConfig;
      if (!wafEngineConfig) return;

      // Magic begins
      this.levelIndexes.detection_paranoia_level = Number(wafEngineConfig.find(({ name }) => name === 'detection_paranoia_level')?.valueEncoded) - 1 || 0;

      this.levelIndexes.executing_paranoia_level = Number(wafEngineConfig.find(({ name }) => name === 'executing_paranoia_level')?.valueEncoded) - 1 || 0;

      this.levelIndexes.blocking_paranoia_level = Number(wafEngineConfig.find(({ name }) => name === 'blocking_paranoia_level')?.valueEncoded) - 1 || 0;

      if (
        this.levelIndexes.detection_paranoia_level === this.levelIndexes.executing_paranoia_level &&
        this.levelIndexes.executing_paranoia_level === this.levelIndexes.blocking_paranoia_level
      ) {
        this.activeTemplate = Number(this.levelIndexes.detection_paranoia_level || 0); // Or any of them
      } else {
        this.activeTemplate = this.levelTemplates.length - 1;
      }
      // Magic ends
    });
  }

  private subscribeToStoreSignals(): void {
    toObservable(this.shield)
      .pipe(filter((shield) => !!shield))
      .subscribe((shield) => {
        this.form.patchValue(
          {
            // Note: this one has to be always updated because of confirmation modal
            wafExecutionMode: shield?.wafExecutionMode ?? WAFExecutionModeEnum.Logging,
            requestBodyLimitAction: shield?.wafRequestBodyLimitAction ?? WAFPayloadLimitActionEnum.Block,
            responseBodyLimitAction: shield?.wafResponseBodyLimitAction ?? WAFPayloadLimitActionEnum.Block,
          },
          {
            emitEvent: false,
          },
        );

        // NOTE: To prevent patching form all the time
        if (!this.isFirstTime) return;

        this.isFirstTime = false;

        // allowed_http_versions
        const existingAllowedHttpVersions = shield?.wafEngineConfig?.find(({ name }) => name === 'allowed_http_versions')?.valueEncoded || '';

        (this.form.get('allowed_http_versions') as FormArray)?.patchValue(existingAllowedHttpVersions ? existingAllowedHttpVersions.split(' ') : []);
        this.formInitialPatchComplete.allowed_http_versions = true;

        // allowed_methods
        const existingAllowedMethods = shield?.wafEngineConfig?.find(({ name }) => name === 'allowed_methods')?.valueEncoded || '';
        (this.form.get('allowed_methods') as FormArray)?.patchValue(existingAllowedMethods ? existingAllowedMethods.split(' ') : []);
        this.formInitialPatchComplete.allowed_methods = true;

        //allowed_request_content_type
        const existingAllowedRequestContentType = shield?.wafEngineConfig?.find(({ name }) => name === 'allowed_request_content_type')?.valueEncoded || '';
        (this.form.get('allowed_request_content_type') as FormArray)?.patchValue(
          existingAllowedRequestContentType ? existingAllowedRequestContentType.split(' ').map((data) => data.replace(/\|/g, '')) : [],
        );
        this.formInitialPatchComplete.allowed_request_content_type = true;
      });
  }

  protected readonly PlanType = PlanType;
}
